/** @jsx jsx */
/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, max-len, no-nested-ternary */
import React, { useEffect, useState } from 'react';
import { css, jsx } from '@emotion/core';
import { PatternInfo } from 'domain/attend/shiftBoard/types';
import { spanMinutes } from 'domain/attend/shiftBoard/time';
import { nameIsTime } from 'domain/attend/shiftBoard/print';
import { holidayLook, mock } from './styles';

/** パレットで選ぶもの。null は選択なし */
export type PaletteSelection =
  | { kind: 'pattern'; patternId: string }
  | { kind: 'holiday'; isLegal: boolean }
  | { kind: 'free' }
  | { kind: 'erase' };

export type FreeTimes = { start: string; end: string; breakMinutes: number | null };

export type PatternColor = { bg: string; fg: string };

/** 帯を折りたたんだかどうか（人ごと・ブラウザごとに覚える） */
const COLLAPSED_KEY = 'shiftBoard.paletteCollapsed';

const styles = {
  // 9/24 に左の縦長カードから、表の上の横長の帯にした（タブレットの半月を 1 画面に収めるため。案D）
  bar: css({
    background: '#fff',
    boxShadow: mock.shadow,
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  }),
  // カードの外の見出し。押すとカードごと開閉する（アコーディオン。9/25）
  header: css({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minHeight: '32px',
  }),
  toggle: css({
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minHeight: '32px',
    padding: '0 4px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 700,
    color: mock.textMain,
    whiteSpace: 'nowrap',
    borderRadius: '4px',
    '&:focus-visible': { outline: `2px solid ${mock.primary}`, outlineOffset: '2px' },
  }),
  chevron: css({
    transition: 'transform .15s',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  }),
  // 登録パターンが多いときは帯の中だけ横にスクロールさせる
  patternList: css({
    display: 'flex',
    gap: '6px',
    flex: '1 1 auto',
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: '2px',
  }),
  fixedList: css({
    display: 'flex',
    gap: '6px',
    flex: 'none',
    paddingLeft: '8px',
    borderLeft: `1px solid ${mock.gray10}`,
  }),
  chip: css({
    flex: 'none',
    borderRadius: '5px',
    padding: '5px 10px',
    minHeight: '40px',
    boxSizing: 'border-box',
    cursor: 'grab',
    userSelect: 'none',
    touchAction: 'pan-x',
    border: '2px solid transparent',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
  }),
  chipName: css({ fontSize: '12px', fontWeight: 700 }),
  chipTime: css({ fontSize: '10px', opacity: 0.85 }),
  input: css({
    width: '64px', boxSizing: 'border-box', border: `1px solid ${mock.gray10}`, borderRadius: '3px', fontSize: '11px', padding: '2px 3px', background: '#fff', color: mock.textMain, fontFamily: 'inherit',
  }),
  guideButton: css({
    flex: 'none', height: '40px', padding: '0 10px', border: `1px solid ${mock.gray10}`, borderRadius: '4px', background: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: '11px', color: mock.textSub, whiteSpace: 'nowrap',
  }),
  guide: css({
    background: mock.primaryM95, padding: '8px 12px', fontSize: '11px', lineHeight: 1.8, color: mock.textSub, display: 'flex', flexWrap: 'wrap', gap: '4px 32px',
  }),
  guideHead: css({ fontWeight: 700, color: mock.textMain }),
  guideAct: css({ fontWeight: 700, color: mock.textMain }),
  sw: css({
    display: 'inline-block', width: '9px', height: '9px', marginRight: '4px', verticalAlign: 'middle',
  }),
};

const autoBreak = (minutes: number): number => (minutes >= 480 ? 60 : minutes >= 360 ? 45 : minutes > 240 ? 15 : 0);
const breakLabel = (m: number): string => (m ? `休憩${m}分` : '休憩なし');

const same = (a: PaletteSelection | null, b: PaletteSelection): boolean => {
  if (!a || a.kind !== b.kind) {
    return false;
  }
  if (a.kind === 'pattern' && b.kind === 'pattern') {
    return a.patternId === b.patternId;
  }
  if (a.kind === 'holiday' && b.kind === 'holiday') {
    return a.isLegal === b.isLegal;
  }
  return true;
};

export type PatternPaletteProps = {
  patterns: Array<PatternInfo>;
  colorOf: (patternId: string | null) => PatternColor;
  distinctionHoliday: boolean;
  selection: PaletteSelection | null;
  onSelect: (s: PaletteSelection) => void;
  free: FreeTimes;
  onFreeChange: (f: FreeTimes) => void;
  /** ドラッグ開始（ポインタ押下） */
  onDragStart: (s: PaletteSelection, e: React.PointerEvent) => void;
  /** 見出しの行の右端に置くもの（更新・設定のアイコンなど。9/25） */
  headerRight?: React.ReactNode;
};

/**
 * シフトパターンのパレット（表の上の帯）
 *
 * 9/24 に左の縦長カードから横長の帯にした（案D。パソコンも同じ形）。
 * 見出し「シフトパターン」はカードの外に置き、押すとカードごと開閉する（9/25。折りたたんだかどうかは覚えておく）。
 * パターンが多いときは帯の中で横にスクロールする。
 * 操作の説明と凡例は「操作ガイド」を押したときだけ出す。
 * 選択中のシフトはチップの枠で示し、いま何が起きるかは上部の一括登録バーが受け持つ。
 */
const PatternPalette: React.FC<PatternPaletteProps> = ({
  patterns, colorOf, distinctionHoliday, selection, onSelect, free, onFreeChange, onDragStart, headerRight,
}) => {
  const [guideOpen, setGuideOpen] = useState(false);
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch (e) {
      return false;
    }
  });
  const setCollapsed = (v: boolean) => {
    setCollapsedState(v);
    try {
      localStorage.setItem(COLLAPSED_KEY, v ? '1' : '0');
    } catch (e) {
      // 覚えられなくても折りたたみはできる
    }
  };
  useEffect(() => {
    if (collapsed) {
      setGuideOpen(false);
    }
  }, [collapsed]);

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const freeSpan = spanMinutes(free.start, free.end, false);
  const freeBreak = free.breakMinutes === null ? autoBreak(freeSpan) : free.breakMinutes;

  const item = (sel: PaletteSelection, bg: string, fg: string, name: string, time: string, key: string, dashed: boolean = false) => (
    <div
      key={key}
      css={styles.chip}
      style={{
        background: bg,
        color: fg,
        borderColor: same(selection, sel) ? mock.secondary : (dashed ? mock.gray20 : 'transparent'),
        borderStyle: same(selection, sel) ? 'solid' : (dashed ? 'dashed' : 'solid'),
      }}
      title={time || name}
      onClick={() => onSelect(sel)}
      onPointerDown={(e) => onDragStart(sel, e)}
    >
      <div css={styles.chipName}>{name}</div>
      {!!time && <div css={styles.chipTime}>{time}</div>}
    </div>
  );

  // 閉じているときに見出しの横に出す「選択中」のカード（9/25 に文字だけの表示から、パレットと同じ色のカードにした）
  let selected: { name: string; time: string; bg: string; fg: string; dashed: boolean } | null = null;
  if (selection && selection.kind === 'pattern') {
    const p = patterns.filter((x) => x.shiftPatternId === selection.patternId)[0];
    if (p) {
      const c = colorOf(p.shiftPatternId);
      const time = p.start && p.end && !nameIsTime(p.name, p.start, p.end) ? `${p.start}–${p.end}` : '';
      selected = {
        name: p.name, time, bg: c.bg, fg: c.fg, dashed: false,
      };
    }
  } else if (selection && selection.kind === 'holiday') {
    selected = {
      // 区別しない店の「公休」は法定で選ばれる（isLegal: true）ので、「（法定）」は区別する店のときだけ付ける（10/6）
      name: selection.isLegal && distinctionHoliday ? '公休（法定）' : distinctionHoliday ? '公休（所定）' : '公休',
      time: '',
      bg: holidayLook(selection.isLegal, distinctionHoliday).bg,
      fg: holidayLook(selection.isLegal, distinctionHoliday).fg,
      dashed: false,
    };
  } else if (selection && selection.kind === 'free') {
    selected = {
      name: 'フリー', time: `${free.start}–${free.end}`, bg: mock.freeBg, fg: mock.freeText, dashed: false,
    };
  } else if (selection && selection.kind === 'erase') {
    selected = {
      name: '割当を外す', time: '', bg: '#fff', fg: mock.gray60, dashed: true,
    };
  }

  return (
    <div>
      <div css={styles.header}>
        <button type="button" css={styles.toggle} onClick={() => setCollapsed(!collapsed)} aria-expanded={!collapsed} aria-controls="shift-pattern-palette">
          <svg css={styles.chevron} style={{ transform: collapsed ? 'rotate(-90deg)' : 'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 9l6 6 6-6" />
          </svg>
          {`シフトパターン（${patterns.length}）`}
        </button>
        {collapsed && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0,
          }}
          >
            <span style={{ fontSize: '11px', color: mock.textSub, whiteSpace: 'nowrap' }}>選択中</span>
            {selected ? (
              <button
                type="button"
                onClick={() => setCollapsed(false)}
                title="パターンを選び直す"
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '6px',
                  minWidth: 0,
                  maxWidth: '360px',
                  padding: '4px 10px',
                  borderRadius: '5px',
                  border: `1px ${selected.dashed ? 'dashed' : 'solid'} ${selected.dashed ? mock.gray20 : 'rgba(0,0,0,.08)'}`,
                  background: selected.bg,
                  color: selected.fg,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                <span style={{
 fontSize: '12px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis',
}}
                >
                  {selected.name}
                </span>
                {!!selected.time && <span style={{ fontSize: '11px', opacity: 0.85 }}>{selected.time}</span>}
              </button>
            ) : (
              <span style={{ fontSize: '12px', color: mock.gray40 }}>なし</span>
            )}
          </div>
        )}
        {headerRight && (
          <div style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px', color: mock.textSub, flex: 'none',
          }}
          >
            {headerRight}
          </div>
        )}
      </div>

      {!collapsed && (
        <div id="shift-pattern-palette" css={styles.bar} style={{ marginTop: '4px' }}>
          <React.Fragment>
            <div css={styles.patternList}>
              {patterns.map((p) => {
                const c = colorOf(p.shiftPatternId);
                const span = p.start && p.end ? spanMinutes(p.start, p.end, p.details.length > 0 && p.details[p.details.length - 1].isEndNextDay) : 0;
                // 名前が時刻そのもののパターンは、下の行に同じ時刻を繰り返さない（9/25）
                const time = p.start && p.end ? (nameIsTime(p.name, p.start, p.end) ? breakLabel(autoBreak(span)) : `${p.start}–${p.end}（${breakLabel(autoBreak(span))}）`) : '時間未設定';
                return item({ kind: 'pattern', patternId: p.shiftPatternId }, c.bg, c.fg, p.name, time, p.shiftPatternId);
              })}
              {patterns.length === 0 && <div style={{ fontSize: '11px', color: mock.textSub, alignSelf: 'center' }}>登録されたパターンがありません。</div>}
            </div>

            <div css={styles.fixedList}>
              {distinctionHoliday
                // 盤面のカードと同じ色（法定は濃い灰色、所定は薄い灰色。10/7 案 B）
                ? [item({ kind: 'holiday', isLegal: true }, holidayLook(true, true).bg, holidayLook(true, true).fg, '公休（法定）', '', 'hol-legal'),
                  item({ kind: 'holiday', isLegal: false }, holidayLook(false, true).bg, holidayLook(false, true).fg, '公休（所定）', '', 'hol')]
                // 区別しない店の「公休」は、旧画面と同じく法定で入れる（10/5）
                : item({ kind: 'holiday', isLegal: true }, mock.gray10, mock.gray60, '公休', '', 'hol')}
              <div
                css={styles.chip}
                style={{ background: mock.freeBg, color: mock.freeText, borderColor: same(selection, { kind: 'free' }) ? mock.secondary : 'transparent' }}
                title="時間を指定して割当（フリーシフト）"
                onClick={() => onSelect({ kind: 'free' })}
                onPointerDown={(e) => onDragStart({ kind: 'free' }, e)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span css={styles.chipName}>フリー</span>
                  <input css={styles.input} type="time" step={1800} value={free.start} aria-label="フリーの開始" onChange={(e) => onFreeChange({ ...free, start: e.target.value })} onPointerDown={stop} onClick={stop} />
                  <span style={{ fontSize: '10px' }}>–</span>
                  <input css={styles.input} type="time" step={1800} value={free.end} aria-label="フリーの終了" onChange={(e) => onFreeChange({ ...free, end: e.target.value })} onPointerDown={stop} onClick={stop} />
                  <span style={{ fontSize: '10px', opacity: 0.8 }}>休憩</span>
                  <input
                    css={styles.input}
                    style={{ width: '48px' }}
                    type="number"
                    min={0}
                    max={240}
                    step={5}
                    value={freeBreak}
                    aria-label="フリーの休憩（分）"
                    title="分（5分単位）"
                    onChange={(e) => onFreeChange({ ...free, breakMinutes: Math.max(0, Math.min(240, Math.round((parseInt(e.target.value || '0', 10)) / 5) * 5)) })}
                    onPointerDown={stop}
                    onClick={stop}
                  />
                </div>
              </div>
              {item({ kind: 'erase' }, '#fff', mock.gray60, '割当を外す', '', 'erase', true)}
            </div>
          </React.Fragment>

          <button type="button" css={styles.guideButton} onClick={() => setGuideOpen(!guideOpen)} aria-expanded={guideOpen}>
            {guideOpen ? '操作ガイドを閉じる' : '操作ガイド'}
          </button>
        </div>
      )}

      {!collapsed && guideOpen && (
        <div css={styles.guide}>
          <div>
            <div css={styles.guideHead}>タブレット</div>
            <div>
              <span css={styles.guideAct}>セルをタップ</span>
              … 選んだシフトを割当
            </div>
            <div>
              <span css={styles.guideAct}>カードをタップ</span>
              … その1件を編集
            </div>
            {/* タブレットではカードを動かせない（ドラッグはマウスだけ）。長押しはセル編集を開く（10/5） */}
            <div>
              <span css={styles.guideAct}>セルを長押し</span>
              … 時間を指定して追加
            </div>
          </div>
          <div>
            <div css={styles.guideHead}>パソコン</div>
            <div>
              <span css={styles.guideAct}>カードをドラッグ</span>
              … 移動
            </div>
            <div>
              <span css={styles.guideAct}>Shift + ドラッグ</span>
              … コピー
            </div>
            <div>
              <span css={styles.guideAct}>セルを右クリック</span>
              … その日の割当を外す
            </div>
          </div>
          <div>
            <div css={styles.guideHead}>見かた</div>
            <div>
              <span css={styles.sw} style={{ background: mock.primaryM95, border: `1px solid ${mock.gray20}` }} />
              背面=希望
            </div>
            <div>
              <span css={styles.sw} style={{ background: '#fff', border: `1px dashed ${mock.primary}` }} />
              保存中（点線）
            </div>
            <div>
              <span css={styles.sw} style={{ background: `${mock.hatch}, ${mock.primaryM90}`, border: '1px solid rgba(0,0,0,.28)' }} />
              支援受入（斜線）
            </div>
          </div>
          <div>
            <div css={styles.guideHead}>&nbsp;</div>
            <div>
              <span css={styles.sw} style={{ background: '#fff', border: `1px dashed ${mock.gray40}` }} />
              他店へ支援（不在）
            </div>
            <div>
              <span css={styles.sw} style={{ background: mock.gray10 }} />
              本部確定（ロック）
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatternPalette;
